import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { outlineOnDarkClass } from '@/lib/ctaStyles';

const BADGES = [
  'SOC 2 Type II',
  'Runs within your security perimeter',
  'Supports all major LLMs',
  'Scales to hundreds of agents',
];

const PLATFORM_CARDS = [
  {
    kicker: 'BUILD',
    title: 'Agent Studio',
    description:
      'Create, test, and deploy agents, skills, and end-to-end workflows across the insurance value chain — no-code, with connectors, policies, and human approvals built into the flow.',
    more: 'Explore Studio →',
    to: '/studio',
    border: 'border-t-secondary',
  },
  {
    kicker: 'WORK',
    title: 'Agent Console',
    description:
      'The industry workbench where people and agents handle claims and underwriting together — with digital workers doing the busywork and humans making the calls that matter.',
    more: 'Explore Console →',
    to: '/console',
    border: 'border-t-ring',
  },
  {
    kicker: 'GOVERN',
    title: 'Agent Lighthouse',
    description:
      'The control plane for every agent in your enterprise — native or third-party, any framework. Discover, enroll, enforce, and walk into any examination with the evidence already assembled.',
    more: 'Explore Lighthouse →',
    to: '/lighthouse',
    border: 'border-t-primary',
  },
];

const DIGITAL_WORKERS = [
  {
    name: 'Athena',
    role: 'Operations Agent',
    description:
      'FNOL and submission intake, digitization, triage and assignment. Turns unstructured intake into structured, routed work.',
  },
  {
    name: 'Atlas',
    role: 'Underwriting Agent',
    description:
      'Risk assessment through quote and issue. Full straight-through on personal lines and small commercial; guided-to-decision on complex risks.',
  },
  {
    name: 'Bella',
    role: 'Adjuster Agent',
    description:
      'Low-complexity claims handled straight through; complex claims guided to a decision an adjuster approves. Salvage, subrogation, and SIU flags surfaced, not buried.',
  },
];

const OPEN_CHIPS = [
  'Guidewire',
  'Duck Creek',
  'Salesforce',
  'ServiceNow',
  'Workday',
  'Claude',
  'GPT',
  'Gemini',
  'open-source models',
  'LangChain',
  'CrewAI',
  'A2A',
  'MCP',
];

const TRUST_ITEMS = [
  {
    title: 'NAIC AI Bulletin',
    description:
      'Evidence fields enforced on every regulated decision; examiner-ready export in one click.',
  },
  {
    title: 'EU AI Act',
    description: 'High-risk-ready control mapping on the same record fabric.',
  },
  {
    title: 'Per-customer isolation',
    description:
      'Dedicated coordinator, database, and storage for every customer — as architecture, not an upsell.',
  },
];

function HeroCTAButtons({
  primaryTo,
  primaryLabel,
  secondaryTo,
  secondaryLabel,
}: {
  primaryTo: string;
  primaryLabel: string;
  secondaryTo: string;
  secondaryLabel: string;
}) {
  return (
    <div className="mt-8 flex flex-wrap items-center gap-3">
      <Button size="lg" asChild>
        <Link to={primaryTo}>{primaryLabel}</Link>
      </Button>
      <Button size="lg" variant="outline" className={outlineOnDarkClass} asChild>
        <Link to={secondaryTo}>{secondaryLabel}</Link>
      </Button>
    </div>
  );
}

export function HomePage() {
  return (
    <>
      <Helmet>
        <title>CriticalBridge.AI - Agentic AI for P&amp;C Insurance</title>
        <meta
          name="description"
          content="CriticalBridge is the agentic AI platform for P&amp;C insurance: build end-to-end claims and underwriting workflows, embedded with digital workers, and govern every agent in your enterprise — including the ones you didn't build."
        />
        <meta
          name="keywords"
          content="agentic AI insurance, claims automation, underwriting automation, AI agent governance, P&C insurance AI"
        />
        <meta property="og:title" content="CriticalBridge.AI - Agentic AI for P&C Insurance" />
        <meta
          property="og:description"
          content="Build end-to-end claims and underwriting workflows, and govern every agent in your enterprise."
        />
        <meta property="og:image" content="https://www.criticalbridge.ai/og-image.png" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.criticalbridge.ai" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-24">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Agentic AI for insurance
          </span>
          <h1 className="mt-4 max-w-[30ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl lg:text-6xl">
            Insurance runs on decisions. Now decisions are accelerated by governed agents.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            CriticalBridge is the agentic AI platform for P&amp;C insurance: build end-to-end claims
            and underwriting workflows, embedded with digital workers, and govern every agent in
            your enterprise — including the ones you didn't build.
          </p>
          <HeroCTAButtons
            primaryTo="/trial"
            primaryLabel="Start the Discovery Trial"
            secondaryTo="/platform"
            secondaryLabel="See the platform"
          />
          <div className="mt-8 flex flex-wrap gap-2.5">
            {BADGES.map((badge) => (
              <span
                key={badge}
                className="rounded-full border border-secondary-foreground/30 px-3.5 py-1.5 font-mono text-xs text-secondary-foreground/70"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* The problem */}
      <section className="border-y bg-card py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            The problem
          </span>
          <h2 className="mt-2 max-w-2xl text-3xl font-bold">
            Task agents everywhere. Outcomes nowhere.
          </h2>
          <p className="mt-4 max-w-[68ch] text-muted-foreground">
            Insurers have bought AI by the tool: document ingestion here, a triage bot there, and
            vendor agents embedded in every SaaS platform. The result is standalone task agents
            multiplying across the value chain with{' '}
            <strong className="text-foreground">
              no central governance, no end-to-end workflow, and no measurable ROI
            </strong>{' '}
            — while claims and underwriting teams get more tools and no better way to work.
          </p>
          <p className="mt-3 max-w-[68ch] font-semibold text-foreground">
            That is not a technology gap. It is a platform gap.
          </p>
        </div>
      </section>

      {/* The platform */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            The platform
          </span>
          <h2 className="mt-2 text-3xl font-bold">
            One platform. From a single agent to the whole fleet.
          </h2>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            Three products on one governed platform — so IT, the business, and the people doing the
            work optimize the same outcomes together.
          </p>

          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {PLATFORM_CARDS.map((card) => (
              <div
                key={card.title}
                className={`rounded-lg border border-t-4 bg-card p-6 ${card.border}`}
              >
                <span className="font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {card.kicker}
                </span>
                <h3 className="mt-3 text-xl font-bold">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {card.description}
                </p>
                <Link
                  to={card.to}
                  className="mt-4 inline-block text-sm font-bold text-primary hover:underline"
                >
                  {card.more}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital workers */}
      <section className="bg-muted py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Digital workers
          </span>
          <h2 className="mt-2 text-3xl font-bold">Digital workers, employed on your workflows.</h2>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            Insurance-native digital workers run inside governed, end-to-end workflows:
            straight-through where the work allows it, guided-to-decision where a person should stay
            in charge — priced by the completed outcome, not by the seat.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {DIGITAL_WORKERS.map((worker) => (
              <div key={worker.name} className="rounded-lg border bg-card p-5">
                <div className="text-lg font-bold">{worker.name}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  {worker.role}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{worker.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button variant="outline" asChild>
              <Link to="/claims">Claims solutions →</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/underwriting">Underwriting solutions →</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Open by design */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Open by design
          </span>
          <h2 className="mt-2 text-3xl font-bold">
            Bring your own AI. Bring your SaaS agents. Keep your core.
          </h2>
          <p className="mt-3 max-w-[68ch] text-muted-foreground">
            CriticalBridge connects to the systems you already run and to whatever models you
            choose. Vendor agents from your SaaS platforms and homegrown agents on any framework
            join the same governed network.
          </p>

          <div className="mt-6 flex flex-wrap gap-2.5">
            {OPEN_CHIPS.map((chip) => (
              <span
                key={chip}
                className="rounded-full border bg-card px-3.5 py-1.5 font-mono text-xs text-muted-foreground"
              >
                {chip}
              </span>
            ))}
          </div>

          <p className="mt-5 max-w-[68ch] font-semibold">
            Insurtech sells tools. SaaS embeds agents. CriticalBridge integrates, automates, and
            governs all of it.
          </p>
        </div>
      </section>

      {/* Governance & trust */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Governance &amp; trust
          </span>
          <h2 className="mt-2 text-3xl font-bold text-secondary-foreground">
            Examination-ready, by architecture.
          </h2>
          <p className="mt-3 max-w-[68ch] text-secondary-foreground/70">
            Every decision an agent touches lands in an immutable, hash-chained audit trail with
            per-decision lineage — and every customer runs on their own isolated instance, so your
            blast radius is yours alone.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TRUST_ITEMS.map((item) => (
              <div
                key={item.title}
                className="rounded-md border border-secondary-foreground/20 p-4"
              >
                <div className="font-semibold text-secondary-foreground">{item.title}</div>
                <div className="mt-1.5 text-sm text-secondary-foreground/60">
                  {item.description}
                </div>
              </div>
            ))}
          </div>

          <Link to="/trust" className="mt-6 inline-block font-bold text-primary hover:underline">
            Read how the trust architecture works →
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Get started
          </span>
          <h2 className="mt-2 text-3xl font-bold text-secondary-foreground">
            Start with what you can see.
          </h2>
          <p className="mt-3 max-w-[68ch] text-secondary-foreground/70">
            The Discovery Trial inventories every agent in your enterprise in 30 days — one
            read-only credential to start, metadata only, nothing deployed. You keep the Shadow
            Agent Report either way.
          </p>
          <HeroCTAButtons
            primaryTo="/trial"
            primaryLabel="Start the Discovery Trial"
            secondaryTo="/contact"
            secondaryLabel="Book a working session"
          />
        </div>
      </section>
    </>
  );
}
